import * as React from 'react';

import * as _ from 'lodash';
import {translate} from "react-i18next";
import {Header, Image, Popup, Step, Icon} from 'semantic-ui-react';

// redux
import {connect} from "react-redux";
import {compose} from "redux";
import {ITranslatedComponent} from "../../../../redux/reducers/generalReducer";
import {IStateProperty} from "../../../BaseColumn";

// components
import Button from '../../../Button';
import ToggleButton from '../../../ToggleButton';
import TextField from '../../../TextField';

// services
import {RoleDto, WorkflowStatusDtoType, WorkflowDtoType} from '../../../../services/Client';
import {IPSLApproval, IRowStatusProps} from '../../../../services/WorkflowUtils';
import {IVirtualizedTableCellComponentProps} from "../../VirtualizedTableInterfaces";
import {selectUserRoles} from '../../../../redux/selectors';
import {IReduxState} from '../../../../redux/IReduxState';
import {getWFStatusImageSrc, handleStepName} from './WorkFlowState';

// helpers

interface IMapStateToProps {
    roles?: RoleDto[] | undefined;
}

interface IOwnProps extends IVirtualizedTableCellComponentProps {
    onApprove?: (newValue: IPSLApproval) => void;
    onChangeWorkflow?: () => void;
}

interface WorkFlowStateProps extends IOwnProps, IMapStateToProps, ITranslatedComponent {
}

interface WorkFlowStateState {
    open: boolean;
    isApproved: boolean | null;
    rejectMessage: string;
    isShowConfirmDialog: boolean;
    selectedWorkflowStep: IStateProperty | null;
}

class NewWorkFlowState extends React.Component<WorkFlowStateProps, WorkFlowStateState> {
    constructor(props: WorkFlowStateProps) {
        super(props);
        this.state = {
            open: false,
            isApproved: null,
            rejectMessage: "",
            isShowConfirmDialog: false,
            selectedWorkflowStep: null
        };
        this.onApprove = this.onApprove.bind(this);
        this.onRejectWorkflowStepChange = this.onRejectWorkflowStepChange.bind(this);
        this.onAcceptWorkflowStepChange = this.onAcceptWorkflowStepChange.bind(this);
        this.onApproveRequest = this.onApproveRequest.bind(this);
        this.closePopup = this.closePopup.bind(this);
        this.resetWorkflow = this.resetWorkflow.bind(this);
        this.callResetWorkflow = this.callResetWorkflow.bind(this);
        this.handleStepClick = this.handleStepClick.bind(this);
        this.renderStep = this.renderStep.bind(this);
    }

    private callResetWorkflow() {
        const value = this.props.value;
        if (this.props.onOptionWFReset) {
            // reset for option
            this.props.onOptionWFReset();
        } else {
            // reset for OB
            const currentValue = _.clone(value);
            currentValue.statusValueState = currentValue.statusValueStates[0].id;
            this.props.onChange(currentValue);
        }
    }

    // TODO: Separate reset from update also for OB
    private resetWorkflow() {
        this.callResetWorkflow();
    }

    private onApprove(isApprovalButton: boolean) {
        if (isApprovalButton) {
            this.setState({
                isApproved: this.state.isApproved === true ? null : true,
                rejectMessage: "",
            });
        } else {
            this.setState({
                isApproved: this.state.isApproved === false ? null : false,
                rejectMessage: "",
            });
        }
    }

    private onRejectWorkflowStepChange() {
        this.closePopup();
    }

    private onAcceptWorkflowStepChange() {
        const currentValue = _.clone(this.props.value);
        if (this.state.selectedWorkflowStep) {
            currentValue.statusValueState = this.state.selectedWorkflowStep.id;
            this.props.onChange(currentValue);
            this.closePopup();
        }
    }

    private onApproveRequest(hasOptionApprovalRights: boolean, canApprove: boolean) {
        if (hasOptionApprovalRights && this.props.onApprove && canApprove) {
            // @ts-ignore warning: approved can't be null, but canApprove verifies to not be null
            this.props.onApprove({
                rejectMessage: this.state.rejectMessage,
                // @ts-ignore warning
                approved: this.state.isApproved,
            });
            this.closePopup();
        }
    }

    private closePopup() {
        this.setState({
            open: false,
            isApproved: null,
            rejectMessage: "",
            isShowConfirmDialog: false,
            selectedWorkflowStep: null
        });
    }

    private handleStepClick(item: IStateProperty, value: IRowStatusProps, suppressWFChange: boolean, isTranslationStepUsed: boolean) {
        if ((!suppressWFChange || isTranslationStepUsed)) {
            if (item.type === WorkflowStatusDtoType.TRANSLATION) {
                this.setState({isShowConfirmDialog: true, selectedWorkflowStep: item});
            } else {
                const currentValue = _.clone(value);
                currentValue.statusValueState = item.id;
                this.props.onChange(currentValue);
            }
        }
    }

    private renderStep(
        item: IStateProperty,
        index: number,
        value: IRowStatusProps,
        suppressWFChange: boolean,
        isTranslationStepUsed: boolean,
        currentStepId: number,
        showBasicWarning: boolean
    ) {
        const stepClasses = "workflow-state-step "
            + (item.id === currentStepId && this.props.readOnly ? "workflow-state-step-active " : "")
            + (suppressWFChange ? "workflow-state-tooltip" : "");

        return (
            <Step
                key={"step-" + index}
                link={true}
                onClick={() => this.handleStepClick(item, value, suppressWFChange, isTranslationStepUsed)}
                disabled={this.props.readOnly}
                active={item.id === currentStepId}
                className={stepClasses}
            >
                <Step.Content>
                    <Step.Title>
                        {item.isResetPoint && <Icon name="backward" disabled={this.props.readOnly} />}
                        {handleStepName(item.hoverText, item.rejectReason, false)}
                    </Step.Title>
                    <Step.Description>
                        <b>{this.props.t("GLOBAL:ROLE")}</b>: {item.role}
                    </Step.Description>
                    {suppressWFChange && !isTranslationStepUsed && (
                        value.invalidTexts ? (
                            <span className="tooltiptext">
                                {this.props.t("OPTIONS_VIEW:EMPTY_TEXTS_WARNING")}
                            </span>
                        ) : (
                            showBasicWarning && (
                                <span className="tooltiptext">
                                    {this.props.t("OPTIONS_VIEW:BASE_OPTION_APPROVAL_WARNING")}
                                </span>
                            )
                        )
                    )}
                </Step.Content>
            </Step>
        );
    }

    render() {
        const value: IRowStatusProps = this.props.value;
        const roles = this.props.roles;
        const inEditMode = this.props.inEditMode;
        const readOnly = this.props.readOnly;
        const {allWorkflows} = this.props;
        let isBasicApproved = true;

        if (value.optionBasic && value.optionBasic.status && allWorkflows) {
            const statusParts = value.optionBasic.status.split('_');
            const wfId = parseInt(statusParts[0], 10);
            const stepId = parseInt(statusParts[1], 10);

            const masterWf = allWorkflows.find(wf => wf.objectId === wfId);

            if (masterWf && masterWf.status) {
                const masterStep = masterWf.status.find((s: any) => s.objectId === stepId);

                if (masterStep) {
                    isBasicApproved = masterStep.type === WorkflowStatusDtoType.APPROVED;
                }
            }
        }

        const showBasicWarning = !isBasicApproved;

        // can reset if status is resetable and user has one of the reset roles
        let canReset = value.statusValueStateResetable
            && (value.workflowType === WorkflowDtoType.OPTION_BASIC ? true : roles && roles.find(function (role: RoleDto) {
                return value.resetRoles.find(function (resetRole: any) {return resetRole.objectId === role.objectId;}) !== undefined;
            }));

        const currentStep = value.statusValueStates && value.statusValueStates.find(function (item: IStateProperty) {
            return item.id === value.statusValueState;
        }) as IStateProperty;

        // special approval only for options
        const hasOptionApprovalRights = value.workflowType === WorkflowDtoType.OPTION_PARTITION
            && currentStep.type === WorkflowStatusDtoType.APPROVAL
            && roles && roles.find(function (role: any) {return role.objectId === currentStep.roleId;}) !== undefined;

        const isSaveChangesDisabled = this.props.workflowReadOnly || this.state.isApproved === null || value.isDependentOverwriteSpecific
            || (this.state.isApproved === false && this.state.rejectMessage.trim() === "");

        let imageSrc: string = getWFStatusImageSrc(this.props.value.statusView);

        const showChangeWfHint = inEditMode && currentStep.type !== WorkflowStatusDtoType.MAINTENANCE;

        const firstRejectState = value.statusValueStates && value.statusValueStates.find(function (state: any) {return state.rejectReason.length > 0;});

        const isTranslationStepUsed = value.statusValueStates
            && value.statusValueStates.find(function (state: any) {
                return state.type === WorkflowStatusDtoType.TRANSLATION;
            }) !== undefined
            && currentStep.type !== WorkflowStatusDtoType.APPROVAL;

        return (
            <Popup
                trigger={
                    <div>
                        <div className="text-align--center">
                            {handleStepName(currentStep.hoverText, firstRejectState ? firstRejectState.rejectReason : "", true)}
                        </div>
                        <Image
                            className="margin-top--10px"
                            centered={true}
                            src={imageSrc}
                            width={30}
                            height={30}
                        />
                    </div>
                }
                flowing={true}
                hoverable={this.state.isApproved === null ? true : false}
                position={'top right'}
                open={this.state.open}
                on={this.state.isApproved === null ? 'hover' : 'click'}
                onOpen={() => this.setState({open: true})}
                onClose={this.closePopup}
                onUnmount={this.closePopup}
                hideOnScroll={true}
                className={'zIndex-9999'}
                content={
                    <>
                        {/** workflow info */}
                        <div style={{maxWidth: '1000px'}} >
                            {value.workFlowName &&
                                <div style={{marginBottom: '15px'}} className='display--flex align-items--center'>
                                    {value.hasChangeWfButton &&
                                        <div className={showChangeWfHint ? 'workflow-state-tooltip' : ''}>
                                            <Button
                                                className={'workflow-state-change-button margin--top-0'}
                                                disabled={!inEditMode || readOnly || currentStep.type !== WorkflowStatusDtoType.MAINTENANCE}
                                                onClick={this.props.onChangeWorkflow}
                                            >
                                                <Icon name={'edit'} size={'large'} />
                                            </Button>
                                            {showChangeWfHint &&
                                                <span className="tooltiptext">{this.props.t("OPTIONS_VIEW:CHANGE_WF_HINT")}</span>
                                            }
                                        </div>
                                    }
                                    <Header className={'margin--top-0'}>
                                        {value.workFlowName}
                                        <span style={{fontWeight: 'normal'}}>
                                            {" - " + (value.workflowResetPointRejection
                                                ? this.props.t("WORKFLOW:REJECT_TO_RESET_POINT_INFO")
                                                : this.props.t("WORKFLOW:REJECT_TO_START_POINT_INFO")
                                            )}
                                        </span>
                                    </Header>
                                </div>
                            }

                            <Step.Group className={"workflow-state-step-group"} size={'tiny'}>
                                {value.statusValueStates.map((item, index) => {
                                    const suppressWFChange =
                                        !this.props.readOnly
                                        && (value.invalidTexts || showBasicWarning)
                                        && item.type !== WorkflowStatusDtoType.TRANSLATION
                                        && value.statusStepIndex !== undefined && value.statusStepIndex > -1 && value.statusStepIndex + 1 === index;

                                    return this.renderStep(item, index, value, !!suppressWFChange, isTranslationStepUsed, value.statusValueState || 0, showBasicWarning);
                                })}
                            </Step.Group>
                        </div>

                        {/** actions  section */}
                        <div>
                            {/** approval */}
                            {hasOptionApprovalRights
                                && !value.isDependentOverwriteSpecific
                                && !this.state.isShowConfirmDialog &&
                                <div className="display--flex">
                                    <div className="display--flex width--100Percent">
                                        <ToggleButton
                                            disabled={this.props.workflowReadOnly}
                                            toggled={this.state.isApproved === true}
                                            icon="check"
                                            onClick={() => this.onApprove(true)}
                                            className={'workflow-state-approval-btn'}
                                        />
                                        <ToggleButton
                                            disabled={this.props.workflowReadOnly}
                                            toggled={this.state.isApproved === false}
                                            icon="ban"
                                            onClick={() => this.onApprove(false)}
                                            className={'workflow-state-approval-btn'}
                                        />
                                        {this.state.isApproved === false &&
                                            <TextField
                                                className="width--100Percent"
                                                style={{margin: "0 20px"}}
                                                onChange={(e, rejectMessage) => this.setState({rejectMessage})}
                                                placeholder={this.props.t("GLOBAL:ENTER_REASON")}
                                                value={this.state.rejectMessage}
                                                multiLine={true}
                                            />
                                        }
                                    </div>
                                    <Button
                                        style={{width: 200}}
                                        disabled={isSaveChangesDisabled}
                                        label={this.props.t("GLOBAL:SAVE_CHANGES")}
                                        icon="save"
                                        onClick={() => this.onApproveRequest(hasOptionApprovalRights, !isSaveChangesDisabled)}
                                        className={'workflow-state-approval-btn'}
                                    />
                                </div>
                            }

                            {/** reject message */}
                            {this.state.rejectMessage.trim() === ''
                                && this.state.isApproved === false
                                && !this.state.isShowConfirmDialog &&
                                <p style={{color: 'red'}}>{this.props.t('GLOBAL:APPROVAL_SAVE_HINT')}</p>
                            }

                            {/** reset */}
                            {canReset && !this.state.isShowConfirmDialog &&
                                <div>
                                    <Button
                                        disabled={this.props.workflowReadOnly}
                                        label={this.props.t("GLOBAL:RESET_WORKFLOW_BUTTON")}
                                        icon="backward"
                                        labelPosition="after"
                                        floated="right"
                                        onClick={() => this.resetWorkflow()}
                                    />
                                </div>
                            }

                            {this.state.isShowConfirmDialog &&
                                <div>
                                    <span className="tooltiptext">
                                        {this.props.t("OPTIONS_VIEW:TRANSLATION_WORKFLOW_STATUS_CONFIRM_MESSAGE")}
                                    </span>
                                    <ToggleButton
                                        toggled={this.state.isShowConfirmDialog}
                                        icon="check"
                                        onClick={this.onAcceptWorkflowStepChange}
                                        className={'workflow-state-approval-btn'}
                                    />
                                    <ToggleButton
                                        toggled={!this.state.isShowConfirmDialog}
                                        icon="ban"
                                        onClick={this.onRejectWorkflowStepChange}
                                        className={'workflow-state-approval-btn'}
                                    />
                                </div>
                            }
                        </div>

                        {/** specific PSL action */}
                        {value.isDependentOverwriteSpecific && !this.state.isShowConfirmDialog &&
                            <div>
                                <Button
                                    disabled={this.props.workflowReadOnly}
                                    label={this.props.t("OPTIONS_VIEW:CREATE_IO_SPECIFIC_OPTION_BTN")}
                                    icon="file"
                                    labelPosition="after"
                                    floated="right"
                                    onClick={this.props.createIOspecificOption}
                                />
                            </div>
                        }
                    </>
                }
            />
        );
    }
}

export default NewWorkFlowState;



// WEBPACK FOOTER //
// ./src/components/Table/components/workflow/WorkFlowStateNewGrid.tsx